import React, { useRef, useState } from 'react';

interface MathAnswerInputProps {
  value: string;
  onChange: (value: string) => void;
  multiline?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  rows?: number;
  isPasteAllowed?: boolean;
  showMathToolbar?: boolean;
}

const MATH_ITEMS = [
  { label: '+', value: '+' },
  { label: '−', value: '−' },
  { label: '×', value: '×' },
  { label: '÷', value: '÷' },
  { label: '=', value: '=' },
  { label: '≠', value: '≠' },
  { label: '<', value: '<' },
  { label: '>', value: '>' },
  { label: '≤', value: '≤' },
  { label: '≥', value: '≥' },
  { label: '√', value: '√' },
  { label: '²', value: '²' },
  { label: '³', value: '³' },
  { label: '½', value: '½' },
  { label: '¼', value: '¼' },
  { label: '¾', value: '¾' },
  { label: '%', value: '%' },
  { label: '°', value: '°' },

  { label: 'mm', value: 'mm' },
  { label: 'cm', value: 'cm' },
  { label: 'm', value: 'm' },
  { label: 'km', value: 'km' },
  { label: 'mg', value: 'mg' },
  { label: 'g', value: 'g' },
  { label: 'kg', value: 'kg' },
  { label: 'mL', value: 'mL' },
  { label: 'L', value: 'L' },
  { label: '₹', value: '₹' },
  { label: '°C', value: '°C' }
];

const MathAnswerInput = ({
  value,
  onChange,
  multiline = false,
  placeholder = 'Enter answer',
  disabled = false,
  className = '',
  rows = 2,
  isPasteAllowed = true,
  showMathToolbar = false
}: MathAnswerInputProps) => {
  const [isToolbarOpen, setIsToolbarOpen] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const selectionRef = useRef({
    start: 0,
    end: 0
  });

  const getActiveInput = () =>
    multiline ? textareaRef.current : inputRef.current;

  /**
   * Store the current cursor/selection position.
   * This allows symbols to be inserted exactly where
   * the student was typing.
   */
  const saveSelection = () => {
    const element = getActiveInput();

    if (!element) return;

    selectionRef.current = {
      start: element.selectionStart ?? value.length,
      end: element.selectionEnd ?? value.length
    };
  };

  /**
   * Insert a symbol at the current cursor position.
   */
  const insertMathSymbol = (symbol: string) => {
    const { start, end } = selectionRef.current;

    const newValue = value.slice(0, start) + symbol + value.slice(end);

    onChange(newValue);
    setIsToolbarOpen(false);
    const newCursorPosition = start + symbol.length;

    requestAnimationFrame(() => {
      const element = getActiveInput();

      if (!element) return;

      element.focus();

      element.setSelectionRange(newCursorPosition, newCursorPosition);

      selectionRef.current = {
        start: newCursorPosition,
        end: newCursorPosition
      };
    });
  };

  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!isPasteAllowed) {
      e.preventDefault();
    }
  };

  const handleToolbarToggle = () => {
    saveSelection();
    setIsToolbarOpen((previous) => !previous);
  };

  const inputClasses = `
    w-full
    bg-theme
    outline-none
    transition-all duration-200
    ${showMathToolbar ? 'pr-14' : ''}
    ${className}
  `;

  return (
    <div className="relative w-full">
      {multiline ? (
        <textarea
          ref={textareaRef}
          value={value}
          rows={rows}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onFocus={saveSelection}
          onClick={saveSelection}
          onKeyUp={saveSelection}
          onSelect={saveSelection}
          onPaste={handlePaste}
          className={inputClasses}
        />
      ) : (
        <input
          ref={inputRef}
          type="text"
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onFocus={saveSelection}
          onClick={saveSelection}
          onKeyUp={saveSelection}
          onSelect={saveSelection}
          onPaste={handlePaste}
          className={inputClasses}
        />
      )}

      {showMathToolbar && (
        <>
          {/* Floating math button */}
          <button
            type="button"
            disabled={disabled}
            aria-label="Math symbols"
            aria-expanded={isToolbarOpen}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleToolbarToggle}
            className={`
              absolute right-2
              ${multiline ? 'bottom-2' : 'top-1/2 -translate-y-1/2'}
              hover:bg-primary/10
              z-20 flex size-9
              items-center justify-center
              rounded-full
              border
              bg-theme
              text-sm font-semibold
              shadow-sm
              transition-all duration-200
              hover:scale-105
              hover:border-primary
              active:scale-95
              ${
                isToolbarOpen
                  ? 'border-primary bg-primary text-white'
                  : 'border-grey1'
              }
            `}
          >
            <span
              className={`
                text-base
                transition-transform duration-200
                ${isToolbarOpen ? 'rotate-45' : ''}
              `}
            >
              {isToolbarOpen ? '+' : '∑'}
            </span>
          </button>

          {/* Math toolbar */}
          <div
            className={`
              absolute right-0 z-30
              ${
                multiline
                  ? 'bottom-[calc(100%+10px)] origin-bottom-right'
                  : 'top-[calc(100%+10px)] origin-top-right'
              }
              w-[340px]
              max-w-[calc(100vw-32px)]
              rounded-2xl
              border border-grey1
              bg-theme
              p-3
              shadow-xl
              transition-all duration-200
              ${
                isToolbarOpen
                  ? 'visible translate-y-0 scale-100 opacity-100'
                  : 'pointer-events-none invisible translate-y-1 scale-95 opacity-0'
              }
            `}
          >
            {/* Header */}
            <div className="mb-3 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold">Math symbols</div>

                <div className="text-xs opacity-60">Tap to insert</div>
              </div>

              <span className="bg-muted rounded-full px-2 py-1 text-[10px] font-medium">
                Maths
              </span>
            </div>

            {/* Symbols */}
            <div className="flex max-h-52 flex-wrap gap-1.5 overflow-y-auto">
              {MATH_ITEMS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => insertMathSymbol(item.value)}
                  className="
                    hover:bg-primary/10 flex h-9
                    min-w-9 items-center
                    justify-center
                    rounded-lg border
                    border-grey1
                    bg-theme
                    px-2 text-sm
                    font-medium transition-all
                    duration-150
                    hover:-translate-y-0.5
                    hover:border-primary
                    hover:text-primary
                    active:scale-95
                  "
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MathAnswerInput;
